import Slider from "react-slick";
import { useEffect, useState } from "react";
import { Link, useFetcher } from "@remix-run/react";

type Collection = {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  imageUrl: string;
};

type Store = {
  id: string;
  name: string;
  handle: string;
  logo: string;
  createdAt: string;
};

export default function LatestCollections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const fetcher = useFetcher<{ success: boolean; collections: Store[] }>();

  useEffect(() => {
    // Fetch latest collections on component mount
    if (fetcher.state === "idle" && !fetcher.data) {
      fetcher.load("/api/latestCollections");
    }
  }, [fetcher]);

  useEffect(() => {
    // Update collections when data is loaded
    if (fetcher.data?.success && fetcher.data.collections) {
      const formattedCollections = fetcher.data.collections.map(
        (collection) => ({
          id: collection.id,
          title: collection.name,
          description: "", // No description in the API data
          buttonText: "SHOP",
          buttonLink: `/${collection.handle}`,
          imageUrl: collection.logo,
        })
      );
      setCollections(formattedCollections);
    }
  }, [fetcher.data]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types
  const SlickButtonFix = ({
    currentSlide,
    slideCount,
    children,
    ...props
  }: {
    currentSlide: number;
    slideCount: number;
    children: React.ReactNode;
    [key: string]: unknown;
  }) => <div {...props}>{children}</div>;

  const settings = {
    arrows: true,
    infinite: true,
    speed: 500,
    centerMode: true,
    centerPadding: "60px",
    slidesToShow: 4,
    slidesToScroll: 1,
    initialSlide: 2,
    prevArrow: (
      <SlickButtonFix currentSlide={0} slideCount={collections.length}>
        <div className="p-2.5 w-full h-full flex justify-center items-center">
          <svg className="mobile-arrow-icon w-full" viewBox="0 0 24 14">
            <path
              className="user-items-list-carousel__arrow-icon-foreground
      mobile-arrow-icon-path"
              d="M7.87012 13L2.00021 7L7.87012 1"
              strokeWidth="2"
              stroke="white"
              fill="none"
            ></path>
            <path
              className="user-items-list-carousel__arrow-icon-foreground
      mobile-arrow-icon-path"
              d="M22.9653 7L3.03948 7"
              strokeWidth="2"
              strokeLinecap="square"
              stroke="white"
              fill="none"
            ></path>
          </svg>
        </div>
      </SlickButtonFix>
    ),
    nextArrow: (
      <SlickButtonFix currentSlide={0} slideCount={collections.length}>
        <div className="p-2.5 w-full h-full flex justify-center items-center">
          <svg
            className="mobile-arrow-icon w-full rotate-180"
            viewBox="0 0 24 14"
          >
            <path
              className="user-items-list-carousel__arrow-icon-foreground
            mobile-arrow-icon-path"
              d="M7.87012 13L2.00021 7L7.87012 1"
              strokeWidth="2"
              stroke="white"
              fill="none"
            ></path>
            <path
              className="user-items-list-carousel__arrow-icon-foreground
            mobile-arrow-icon-path"
              d="M22.9653 7L3.03948 7"
              strokeWidth="2"
              strokeLinecap="square"
              stroke="white"
              fill="none"
            ></path>
          </svg>
        </div>
      </SlickButtonFix>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  // @ts-expect-error - This is a hack to prevent SSR issues with the Slider component
  const SliderComponent =
    typeof window === "undefined" ? Slider.default : Slider;

  return (
    <section className="bg-white py-8">
      <div className="px-0">
        {/* Section title */}
        <h2 className="text-3xl uppercase mb-6 px-4 md:px-6 md:mb-8">
          Latest collections
        </h2>

        {/* A horizontal scroll container for the cards */}
        <div className="space-x-4 w-full mb-16">
          <SliderComponent {...settings}>
            {collections.map((item, idx) => (
              <div key={idx} className="px-2">
                <div className="w-full flex-shrink-0 bg-[#d9d9d9] border-gray-200 rounded-lg p-4 text-center">
                  {/* Card image */}
                  <div className="w-full aspect-square overflow-hidden rounded mb-4">
                    <Link to={item.buttonLink}>
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </Link>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-medium mb-4">{item.title}</h3>
                  {/* Button */}
                  <Link
                    to={item.buttonLink}
                    className="inline-block bg-black text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition-colors uppercase"
                  >
                    {item.buttonText}
                  </Link>
                </div>
              </div>
            ))}
          </SliderComponent>
        </div>
      </div>
    </section>
  );
}
